import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { api } from '@/lib/api';
import { Send, ArrowRight, Loader2, Zap } from 'lucide-react';

const FIATS = [
  { code: 'USD', sym: '$', rate: 1.0 },
  { code: 'EUR', sym: '€', rate: 0.92 },
  { code: 'GBP', sym: '£', rate: 0.79 },
  { code: 'NOK', sym: 'kr', rate: 10.72 },
  { code: 'KES', sym: 'KSh', rate: 129.4 },
  { code: 'ETB', sym: 'Br', rate: 57.2 },
  { code: 'SAR', sym: 'SR', rate: 3.75 },
  { code: 'AED', sym: 'AED', rate: 3.67 },
];

const FEE_RATE = 0.01; // 1%

/**
 * Interactive Quick Send quote widget. Uses the public live FX feed to show a
 * transparent 1% fee + recipient amount, then funnels to register (logged out)
 * or the Send page (logged in). Safe to render on public pages.
 */
const QuickSendWidget = ({ variant = 'light' }) => {
  const navigate = useNavigate();
  const [rates, setRates] = useState(FIATS);
  const [amount, setAmount] = useState('1000');
  const [from, setFrom] = useState('USD');
  const [to, setTo] = useState('KES');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get('/market/rates');
        if (active && Array.isArray(data?.rates)) {
          const fiat = data.rates.filter((r) => r.type === 'fiat' && r.rate);
          if (fiat.length) setRates(fiat);
        }
      } catch (e) {
        /* keep seed rates */
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const rateMap = useMemo(() => {
    const m = {};
    rates.forEach((r) => { m[r.code] = r.rate; });
    return m;
  }, [rates]);

  const symOf = (code) => (rates.find((r) => r.code === code)?.sym) || (FIATS.find((f) => f.code === code)?.sym) || '';

  const amt = parseFloat(amount) || 0;
  const fee = amt * FEE_RATE;
  const rateFrom = rateMap[from] || 1;
  const rateTo = rateMap[to] || 1;
  const fxRate = rateTo / rateFrom;
  const recipientGets = amt * fxRate;

  const proceed = () => {
    const token = localStorage.getItem('token');
    if (token) navigate(`/send?to=${to}&from=${from}&amount=${amt || ''}`);
    else navigate('/register');
  };

  const dark = variant === 'dark';
  const wrap = dark ? 'bg-white/5 border-white/10 text-white' : 'bg-white';

  return (
    <Card data-testid="quick-send-widget" className={`p-6 md:p-7 shadow-[0_8px_30px_rgb(0,0,0,0.06)] ${wrap}`}>
      <div className="flex items-center gap-2 mb-5">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-accent/15 text-accent">
          <Zap className="w-4 h-4" />
        </span>
        <h3 className="text-xl font-bold tracking-tight">Quick Send</h3>
        <span className={`ml-auto text-xs ${dark ? 'text-white/50' : 'text-muted-foreground'}`}>Live rate · 1% fee</span>
      </div>

      <div className="space-y-4">
        <div>
          <Label className={dark ? 'text-white/70' : ''}>You send</Label>
          <div className="flex gap-2 mt-1">
            <Input
              data-testid="quick-send-amount"
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="font-mono text-lg"
              placeholder="0.00"
            />
            <Select value={from} onValueChange={setFrom}>
              <SelectTrigger className="w-28" data-testid="quick-send-from">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {rates.filter((r) => r.code !== to).map((r) => (
                  <SelectItem key={r.code} value={r.code}>{r.code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center justify-center">
          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${dark ? 'bg-white/10' : 'bg-muted'}`}>
            <ArrowRight className="w-4 h-4 rotate-90" />
          </span>
        </div>

        <div>
          <Label className={dark ? 'text-white/70' : ''}>Recipient gets</Label>
          <div className="flex gap-2 mt-1">
            <div
              data-testid="quick-send-recipient-gets"
              className={`flex-1 h-10 rounded-md border px-3 flex items-center font-mono text-lg ${dark ? 'border-white/15 bg-white/5' : 'bg-muted/40'}`}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : recipientGets.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <Select value={to} onValueChange={setTo}>
              <SelectTrigger className="w-28" data-testid="quick-send-to">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {rates.filter((r) => r.code !== from).map((r) => (
                  <SelectItem key={r.code} value={r.code}>{r.code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className={`text-sm space-y-1 rounded-lg p-3 ${dark ? 'bg-white/5' : 'bg-muted/40'}`}>
          <div className="flex justify-between">
            <span className={dark ? 'text-white/60' : 'text-muted-foreground'}>Fee (1%)</span>
            <span className="font-mono" data-testid="quick-send-fee">{symOf(from)} {fee.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          <div className="flex justify-between">
            <span className={dark ? 'text-white/60' : 'text-muted-foreground'}>Exchange rate</span>
            <span className="font-mono" data-testid="quick-send-rate">1 {from} = {fxRate.toLocaleString(undefined, { maximumFractionDigits: 4 })} {to}</span>
          </div>
          <div className="flex justify-between font-semibold pt-1 border-t border-dashed border-current/10">
            <span>Total to pay</span>
            <span className="font-mono" data-testid="quick-send-total">{symOf(from)} {(amt + fee).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        </div>

        <Button
          data-testid="quick-send-cta"
          onClick={proceed}
          className="w-full h-11 gap-2"
          style={{ backgroundColor: '#22c55e', color: '#ffffff' }}
        >
          <Send className="w-4 h-4" /> Send Money
        </Button>
        <p className={`text-center text-xs ${dark ? 'text-white/40' : 'text-muted-foreground'}`}>
          Transparent pricing · No hidden fees · Real-time rates
        </p>
      </div>
    </Card>
  );
};

export default QuickSendWidget;
